import { Injectable } from '@angular/core';
import { ProductInterface } from '../../../shared/types/product.interface';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  retry,
  switchMap,
} from 'rxjs';
import { AppService } from '../../../shared/services/app.service';
import { HttpBaseResponse } from '../../../shared/types/http-response.interface';
import { CategoryInterface } from '../../../shared/types/category.interface';
import { MediaFilterType, ModelResource } from '../../../shared/types/enums';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  constructor(private http: HttpClient, private appService: AppService) {}

  getFilterCategories(brandId: number) {
    return this.http.post<CategoryInterface[]>(
      `${environment.urlApi}ard/homepage/brandCategories`,
      {
        brandId: brandId,
      }
    );
  }

  getBrands() {
    const body = {
      filterResourceModel: ModelResource.Category,
      filterTypeId: 2,
    };

    return this.http
      .post<HttpBaseResponse<CategoryInterface>>(
        `${environment.urlApi}category/categories/categories`,
        body
      )
      .pipe(
        retry(2),
        switchMap((response) => {
          const brands = response.items;
          // get media request for each brand
          const mediaRequests = brands.map((brand) =>
            this.appService.getMedia(
              brand.id,
              ModelResource.Tag,
              false,
              MediaFilterType.ICON
            )
          );
          // get media response for each brand
          return forkJoin(mediaRequests).pipe(
            map((mediaResponses) => {
              const brandsWithMedia = brands.map((brand, index) => {
                return {
                  ...brand,
                  src: mediaResponses[index][0]?.MediaFile?.urlSigned || null,
                };
              });
              return brandsWithMedia;
            })
          );
        }),
        catchError((error) => {
          console.log(error);
          return of([]);
        })
      );
  }

  getBrandById(brandId: string) {
    return this.http
      .post<CategoryInterface>(`${environment.urlApi}category/categories/get`, {
        search: brandId,
        field: 'id',
      })
      .pipe(
        retry(2),
        catchError((error) => of(null))
      );
  }
  getBrandByArdId(ardId: string) {
    return this.http
      .post<CategoryInterface>(`${environment.urlApi}category/categories/get`, {
        search: ardId,
        field: 'field1',
      })
      .pipe(
        retry(2),
        catchError((error) => of(null))
      );
  }

  getProductDetails(id: string): Observable<ProductInterface | null> {
    return this.appService.getProductByExternalId(id).pipe(
      switchMap((product) => {
        if (!product) {
          return of(null);
        }
        return this.appService.getMedia(product?.id, ModelResource.Device).pipe(
          map((mediaResponse) => ({
            ...product,
            imageUrl: mediaResponse[0]?.MediaFile?.urlSigned || null,
          }))
        );
      })
    );
  }

  getProductsByBrand(
    brandId: string,
    page: number,
    limit: number = 24,
    order?: string,
    categoryId?: number | undefined
  ): Observable<HttpBaseResponse<ProductInterface> | null> {
    const searchField = [{ value: brandId, field: 'brandId', operator: '=' }];

    if (categoryId !== undefined) {
      searchField.push({
        value: categoryId.toString(),
        field: 'fieldNum1',
        operator: '=',
      });
    }

    const body = {
      limit: limit,
      page: page,
      order: order,
      searchField: searchField,
    };
    return this.appService.getProducts(body);
  }

  transformBrandURL(text: string): string {
    return text.trim().replace(/\s+/g, '-').toLowerCase();
  }
}
